/* eslint-disable react-hooks/exhaustive-deps */
import { Flex, Stack, Text, SimpleGrid } from '@mantine/core'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useCallback, useEffect, useState } from 'react'
import { getChartDataUser, getCountUser, getNewUserMetics } from 'src/apis/chartAPI'
import ApexChart from 'src/components/Chart/Chartline'
import CustomDatePickerInput from 'src/components/CustomComponents/CustomDatePickerInput'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { color, formatDate } from 'src/variable/constant'
import { NewUsersCardApex, NewUserStats } from 'src/components/Chart/NewUsersCard'
import { TopFeatureUsageCard } from 'src/components/Chart/TopFeatureUsageCard'

dayjs.extend(customParseFormat)

function Home() {
	const { openError } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [totalNewUsersByFilterDate, setTotalNewUsersByFilterDate] = useState<{
		label: string,
		value: number,
	} | null>(null)
	const [averageUsersPerDay, setAverageUsersPerDay] = useState<number | null>(null)
	
	const [newUserMetric, setNewUserMetric] = useState<NewUserStats>({
		today: 0,
		week: 0,
		month: 0,
		all: 0,
		avg: 0,
	})

	const [data, setData] = useState({
		categories: [],
		data: [],
	})
	const [date, setDate] = useState({
		start_time: null,
		end_time: null,
	}) as any
	const [users, setUser] = useState(0)
	const handleGetDataUsers = useCallback(async (params: {
		start_time?: string,
		end_time?: string,
	} = {}) => {
		openLoadingContext()
		try {
			const res = await getChartDataUser({
				type: 'date',
				...params,
			})
			if (res) {
				const { object } = res?.data?.results || []
				// console.log(object);
				const chartData = object?.data || []
				const users = chartData.map((item) => Number(item.count))
				const date = chartData.map((item) => dayjs(item.date).format(formatDate))
				if (params.start_time && params.end_time) {
					const totalNewUsers = chartData.reduce((pre, cur) => pre + Number(cur.count), 0)
					setTotalNewUsersByFilterDate({
						label: `${dayjs(params.start_time, formatDate).format(formatDate)} - ${dayjs(params.end_time, formatDate).format(formatDate)}`,
						value: totalNewUsers
					})
				} else {
					const firstDate = chartData[0]?.date
					const lastDate = chartData[chartData.length - 1]?.date
					setTotalNewUsersByFilterDate({
						label: firstDate && lastDate
							? `${dayjs(firstDate).format(formatDate)} - ${dayjs(lastDate).format(formatDate)}`
							: 'All time',
						value: object?.total_users ?? chartData.reduce((pre, cur) => pre + Number(cur.count), 0)
					})
				}
				setData({
					data: users,
					categories: date,
				})
				setAverageUsersPerDay(object?.average_users_per_day)
			}
			// console.log('object', res)
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	const handleCountDataUsers = useCallback(async () => {
		try {
			const res = await getCountUser({})
			if (res) {
				const { object } = res?.data?.results || []

				setUser(object?.users_count || 0)
			}
			// console.log('object', res)
		} catch (error) {
			openError(error)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	const handleGetNewUserMetrics = useCallback(async () => {
		try {
			const res = await getNewUserMetics({})
			if (res) {
				const { object } = res?.data?.results || []
				// console.log(object);
				setNewUserMetric(object)
			}
			// console.log('object', res)
		} catch (error) {
			openError(error)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	const handleChangeDate = useCallback(
		(key) => (e) => {
			const start_time = dayjs(date.start_time).valueOf()
			const end_time = dayjs(date.end_time).valueOf()
			const current_time = dayjs(e).valueOf()
			if (key === 'start_time' && current_time > end_time)
				return setDate({ end_time: null, [key]: e })
			if (key === 'end_time' && current_time < start_time)
				return setDate({ start_time: null, [key]: e })
			return setDate((pre) => ({ ...pre, [key]: e }))
		},
		[date],
	)
	useEffect(() => {
		handleCountDataUsers()
		handleGetNewUserMetrics()
	}, [])
	useEffect(() => {
		if (date.end_time && date.start_time) {
			const submitDate = {
				start_time: dayjs(date.start_time).format(formatDate),
				end_time: dayjs(date.end_time).format(formatDate),
			}
			handleGetDataUsers(submitDate)
		}
		if (!date.end_time && !date.start_time) {
			handleGetDataUsers()
		}
	}, [date])
	return (
		<Stack
			sx={{
				width: '100%',
			}}
		>
			<Stack
				spacing={8}
				sx={{
					justifyContent: 'center',
					left: -16,
					padding: '18px 30px',
					background: 'white',
					width: 'calc(100% + 32px)',
					'@media (max-width:900px)': {
						display: 'none',
					},
				}}
			>
				<Text color={color.Orange} sx={{ fontWeight: 700, fontSize: 30 }}>
					UniVini
				</Text>
				<Text color={color.Green} sx={{ fontWeight: 700, fontSize: 25 }}>
					Exchange Languages, Build Networks and Travel
				</Text>
				<Text color={color.Blue} sx={{ fontWeight: 700, fontSize: 27 }}>
					Users: {users.toLocaleString('en-US')}
				</Text>
			</Stack>
			<Stack spacing={12}>
				<Flex
					gap={16}
					wrap="wrap"
					sx={{
						maxWidth: 700,
					}}
				>
					<Stack sx={{ width: 300, '@media (max-width:600px)': { width: '100%' } }}>
						<CustomDatePickerInput
							clearable
							label="Start date"
							valueFormat={formatDate}
							value={date.start_time}
							maxDate={new Date()}
							onChange={handleChangeDate('start_time')}
						/>
					</Stack>
					<Stack sx={{ width: 300, '@media (max-width:600px)': { width: '100%' } }}>
						<CustomDatePickerInput
							clearable
							label="End date"
							valueFormat={formatDate}
							value={date.end_time}
							maxDate={new Date()}
							onChange={handleChangeDate('end_time')}
						/>
					</Stack>
				</Flex>
				<Stack spacing={4}>
					{/* <Text color={color.Blue} sx={{ fontWeight: 400, fontSize: 16 }}>
						Selected Date: {dayjs(date.start_time).format(formatDate)} - {dayjs(date.end_time).format(formatDate)}
					</Text> */}
					<Text color={color.Orange} sx={{ fontWeight: 800, fontSize: 16 }}>
						Total New Users(By Filter Date): {" "} 
						{totalNewUsersByFilterDate ? `${totalNewUsersByFilterDate.label}: ${totalNewUsersByFilterDate.value.toLocaleString('en-US')}` : 'Loading...'}
					</Text>
					<Text color={color.Orange} sx={{ fontWeight: 800, fontSize: 16 }}>
						Average  Users(By Filter Date): {" "} 
						{averageUsersPerDay !== null ? averageUsersPerDay.toLocaleString('en-US') : 'Loading...'}
					</Text>
				</Stack>
			</Stack>
			<ApexChart {...data} />

			<SimpleGrid
				cols={2}
				spacing="lg"
				breakpoints={[
					{ maxWidth: 'sm', cols: 1 },
				]}
			>
				<NewUsersCardApex data={newUserMetric} />
				<TopFeatureUsageCard />
			</SimpleGrid>
		</Stack>
	)
}

export default Home
